import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import { CALENDAR_CONFIG_BY_RESOURCENAME } from 'volta/components/forms/calendar-editor';
import { SupplierCalendarDayResourceName } from 'volta/models/supplier-calendar-day';
import {
  WarehouseCalendarDayEvents,
  WarehouseCalendarDayResourceName
} from 'volta/models/warehouse-calendar-day';
import {
  WorkshopCalendarDayEvents,
  WorkshopCalendarDayResourceName
} from 'volta/models/workshop-calendar-day';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { distinct, indexBy } from 'volta/utils/array-utils';
import { ISO_DATE_FORMAT, isWeekendDay } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { btFilter, eqFilter } from 'volta/utils/filters-utils';

import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { SupplierCalendarDayEvents } from '../models/supplier-calendar-day';

import type { TError } from 'volta/utils/error-utils';
import type { IResourceName } from 'volta/models/base-model';
import type EventStreamService from './event-stream';

import type StoreService from './store';
import type BvFlashService from './bv-flash';
import type {
  TCalendarDay,
  TDate,
  TSupplierCalendarCmdResult,
  TWarehouseCalendarCmdResult,
  TWorkshopCalendarCmdResult
} from 'volta/models/types/calendar';
import type Owner from "@ember/owner";

export default class CalendarService extends Service {
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;

  @tracked cache: Record<string, Record<string, TCalendarDay[]>> = {};

  constructor(owner: Owner) {
    super(owner);

    this.eventStream.addHandler(
      WarehouseCalendarDayResourceName.plural,
      WarehouseCalendarDayEvents.CalendarUpdated,
      this.onWarehouseCalendarUpdated,
      this
    );

    this.eventStream.addHandler(
      WarehouseCalendarDayResourceName.plural,
      WarehouseCalendarDayEvents.DaysReset,
      this.onWarehouseDaysReset,
      this
    );

    this.eventStream.addHandler(
      WarehouseCalendarDayResourceName.plural,
      WarehouseCalendarDayEvents.WarehouseCalendarDayReset,
      this.onWarehouseCalendarDayReset,
      this
    );

    this.eventStream.addHandler(
      SupplierCalendarDayResourceName.plural,
      SupplierCalendarDayEvents.CalendarUpdated,
      this.onSupplierCalendarUpdated,
      this
    );

    this.eventStream.addHandler(
      SupplierCalendarDayResourceName.plural,
      SupplierCalendarDayEvents.DaysReset,
      this.onSupplierDaysReset,
      this
    );

    this.eventStream.addHandler(
      WorkshopCalendarDayResourceName.plural,
      WorkshopCalendarDayEvents.DaysReset,
      this.onWorkshopDaysReset,
      this
    );

    this.eventStream.addHandler(
      SupplierCalendarDayResourceName.plural,
      SupplierCalendarDayEvents.SupplierCalendarDayReset,
      this.onSupplierCalendarDayReset,
      this
    );
    this.eventStream.addHandler(
      WorkshopCalendarDayResourceName.plural,
      WorkshopCalendarDayEvents.CalendarUpdated,
      this.onWorkshopCalendarUpdated,
      this
    );
    this.eventStream.addHandler(
      WorkshopCalendarDayResourceName.plural,
      WorkshopCalendarDayEvents.WorkshopCalendarDayReset,
      this.onWorkshopCalendarDayReset,
      this
    );
  }

  willDestroy() {
    super.willDestroy();
    this.eventStream.removeHandlers(this);
  }

  buildDefaultState(date: TDate, weekendDays?: number[]): TCalendarDay {
    return {
      openForDemand: !isWeekendDay(date, weekendDays),
      openForReception: !isWeekendDay(date, weekendDays),
      isDefault: true
    } as TCalendarDay;
  }

  fetchCalendarDays = restartableTask(
    async (
      resourceName: IResourceName,
      entityId?: string,
      year: number = new Date().getFullYear()
    ) => {
      const filter: Record<string, string> = {};
      const { singular } = resourceName;
      const entityKey = CALENDAR_CONFIG_BY_RESOURCENAME[singular]?.entityId;

      if (entityId && entityKey) {
        filter[entityKey] = eqFilter(entityId);
      }

      filter['date'] = btFilter([
        moment.utc().year(year).startOf('year').format(ISO_DATE_FORMAT),
        moment.utc().year(year).endOf('year').format(ISO_DATE_FORMAT)
      ]);

      try {
        if (entityId && this.cache[entityId]?.[year]) {
          return this.cache[entityId]?.[year];
        }

        const days = (
          await this.store.query(resourceName.singular, {
            filter,
            page: { offset: 0, limit: 366 }
          })
        ).slice() as TCalendarDay[];

        // we only cache the result if query by entityId
        // with on specified dates
        if (entityId) {
          const cache = this.updateCache(entityId, year, days);
          return cache[entityId]?.[year];
        }
        return days;
      } catch (error) {
        defaultErrorHandler(error as TError);
        return;
      }
    }
  );

  private onWarehouseCalendarUpdated(payload: TWarehouseCalendarCmdResult) {
    this.onCalendarUpdated(WarehouseCalendarDayResourceName, payload);
  }

  private onWarehouseDaysReset(payload: { warehouseId: string; dates: string[] }) {
    this.onDaysRemove(payload.warehouseId, payload.dates);
  }

  private onWarehouseCalendarDayReset(payload: TWarehouseCalendarCmdResult) {
    this.onDayReset(WarehouseCalendarDayResourceName, payload);
  }

  private onWorkshopCalendarUpdated(payload: TWorkshopCalendarCmdResult) {
    this.onCalendarUpdated(WorkshopCalendarDayResourceName, payload);
  }

  private onWorkshopDaysReset(payload: { workshopId: string; dates: string[] }) {
    this.onDaysRemove(payload.workshopId, payload.dates);
  }

  private onWorkshopCalendarDayReset(payload: TWorkshopCalendarCmdResult) {
    this.onDayReset(WorkshopCalendarDayResourceName, payload);
  }

  private onSupplierCalendarUpdated(payload: TSupplierCalendarCmdResult) {
    this.onCalendarUpdated(SupplierCalendarDayResourceName, payload);
  }

  private onSupplierDaysReset(payload: { supplierId: string; dates: string[] }) {
    this.onDaysRemove(payload.supplierId, payload.dates);
  }

  private onSupplierCalendarDayReset(payload: TSupplierCalendarCmdResult) {
    this.onDayReset(SupplierCalendarDayResourceName, payload);
  }

  private onDaysUpdate(entityId: string, days: TCalendarDay[]) {
    const years = distinct(days.map((day) => day.date.getFullYear()));

    years.forEach((year) => {
      const yearDays = days.filter((day) => day.date.getFullYear() === year);
      const cached: TCalendarDay[] | undefined = this.cache[entityId]?.[year];

      if (!cached) {
        return yearDays;
      }

      const existing = indexBy(cached ?? [], 'formattedDate');
      yearDays.forEach((d) => {
        existing[d.formattedDate] = d;
      });
      const cache = this.updateCache(entityId, year, Object.values(existing));
      return cache[entityId]?.[year];
    });
  }

  private onDaysRemove(entityId: string, dates: string[]) {
    const years = distinct(dates.map((day) => moment.utc(day).year()));

    years.forEach((year) => {
      const yDates = new Set(dates.filter((day) => moment.utc(day).year() === year));
      const cached = this.cache[entityId]?.[year];
      if (!cached) {
        return [];
      }
      const days = cached.filter((d) => !yDates.has(d.formattedDate));
      const cache = this.updateCache(entityId, year, days);
      return cache[entityId]?.[year];
    });
  }

  private onCalendarUpdated(resourceName: IResourceName, payload: object) {
    const days = normalize(resourceName.singular, payload) as TCalendarDay[];
    const entityKey = CALENDAR_CONFIG_BY_RESOURCENAME[resourceName.singular]?.entityId;
    if (entityKey && days && days[0]) {
      this.onDaysUpdate(days[0][entityKey as keyof TCalendarDay] as string, days);
    }
  }

  private onDayReset(
    resourceName: IResourceName,
    payload: TWorkshopCalendarCmdResult | TSupplierCalendarCmdResult | TWarehouseCalendarCmdResult
  ) {
    const { attributes } = payload.data;
    const { singular } = resourceName;
    const entityKey = CALENDAR_CONFIG_BY_RESOURCENAME[singular]?.entityId;

    if (entityKey && attributes.date) {
      const entity = attributes[entityKey as keyof typeof attributes];
      if (entity) {
        this.onDaysRemove(entity, [attributes.date]);
      }
    }
  }

  private updateCache(entityId: string, year: number, days: TCalendarDay[]) {
    const { cache } = this;
    if (!cache[entityId]) {
      cache[entityId] = { [year]: days };
    }
    cache[entityId]![year] = days;
    this.cache = cache;
    return cache;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar-service': CalendarService;
  }
}
